import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  createContext,
  useContext,
  useRef,
} from "react";
import { Table } from "antd";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { StyledTableContainer } from "./styles";
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { closestCenter, DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";

// Mirrors common/elements/Table's floor for the same reason.
const MIN_COLUMN_WIDTH = 50;

const DragIndexContext = createContext({ active: -1, over: -1 });

const dragActiveStyle = (dragState, id) => {
  const { active, over } = dragState;
  if (over && id === over && active !== over) {
    return { borderInlineStart: '1px dashed gray' };
  }
  return {};
};

const TableBodyCell = props => {
  const dragState = useContext(DragIndexContext);
  return <td {...props} style={{ ...props.style, ...dragActiveStyle(dragState, props.id) }} />;
};

// const TableHeaderCell = props => {
//   const dragState = useContext(DragIndexContext);
//   const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: props.id });
//   const style = {
//     ...props.style,
//     cursor: 'pointer',
//     ...(isDragging ? { position: 'relative', zIndex: 9999, userSelect: 'none' } : {}),
//     ...dragActiveStyle(dragState, props.id),
//   };
//   return <th {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
// };

// Drag-to-resize: dragging the divider between two ADJACENT columns trades
// width between them — grows one, shrinks the other by the exact same
// amount — so the table's total width never changes, matching normal
// spreadsheet-style column resize (same give-and-take semantics as
// common/elements/Table's useAdjacentColumnResize). Only a column with a
// column to its right (`nextId`) gets a handle — the last column resizes
// via the previous column's handle instead, the same physical divider.
// `thRefs` is the shared map of dragKey -> <th> DOM node (populated by each
// header cell's ref below), read/mutated directly here for a live drag;
// resize is independent of reorder, so this hook has no drag-and-drop
// dependency of its own. On mouseup it dispatches a bubbling 'col-resize'
// custom event carrying BOTH columns' final widths (caught by DataTable's
// own listener) rather than calling back directly, so it doesn't need to
// know how the resulting widths get persisted.
const useAdjacentColumnResizeHandle = (id, nextId, thRefs) => {
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startLeftWidth = useRef(0);
  const startRightWidth = useRef(0);

  return (e) => {
    if (!nextId) return;
    e.stopPropagation();
    e.preventDefault();

    isResizing.current = true;
    startX.current = e.clientX;
    startLeftWidth.current = thRefs.current[id]?.offsetWidth ?? 100;
    startRightWidth.current = thRefs.current[nextId]?.offsetWidth ?? 100;

    const clampDiff = (diff) => {
      const minDiff = MIN_COLUMN_WIDTH - startLeftWidth.current;
      const maxDiff = startRightWidth.current - MIN_COLUMN_WIDTH;
      return Math.max(minDiff, Math.min(diff, maxDiff));
    };

    const applyWidths = (leftWidth, rightWidth) => {
      const leftEl = thRefs.current[id];
      const rightEl = thRefs.current[nextId];
      if (leftEl) {
        leftEl.style.width = `${leftWidth}px`;
        leftEl.style.minWidth = `${leftWidth}px`;
        leftEl.style.maxWidth = `${leftWidth}px`;
      }
      if (rightEl) {
        rightEl.style.width = `${rightWidth}px`;
        rightEl.style.minWidth = `${rightWidth}px`;
        rightEl.style.maxWidth = `${rightWidth}px`;
      }
    };

    const onMouseMove = (moveEvent) => {
      if (!isResizing.current) return;
      const diff = clampDiff(moveEvent.clientX - startX.current);
      applyWidths(startLeftWidth.current + diff, startRightWidth.current - diff);
    };

    const onMouseUp = (upEvent) => {
      if (!isResizing.current) return;
      isResizing.current = false;

      const diff = clampDiff(upEvent.clientX - startX.current);
      const newLeftWidth = startLeftWidth.current + diff;
      const newRightWidth = startRightWidth.current - diff;

      thRefs.current[id]?.dispatchEvent(
        new CustomEvent('col-resize', {
          bubbles: true,
          detail: { widths: { [id]: newLeftWidth, [nextId]: newRightWidth } },
        })
      );

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };
};

const ResizeHandle = ({ onMouseDown }) => (
  <span
    onMouseDown={onMouseDown}
    onPointerDown={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}
    style={{
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: 6,
      cursor: 'col-resize',
      zIndex: 10,
      userSelect: 'none',
    }}
  />
);

// Full Config-mode header cell: reorder (via `@dnd-kit`'s useSortable, so
// this must be rendered inside a DndContext) + resize together. Only used
// while `draggable` (i.e. Config mode) is on.
const ResizableHeaderCell = (props) => {
  const { id, nextId, thRefs, style, children, ...restProps } = props;

  const dragState = useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id });
  const onResizeMouseDown = useAdjacentColumnResizeHandle(id, nextId, thRefs);

  const registerRef = (node) => {
    if (thRefs) thRefs.current[id] = node;
    setNodeRef(node);
  };

  const thStyle = {
    ...style,
    cursor: 'pointer',
    position: 'relative',
    ...(isDragging ? { zIndex: 9999, userSelect: 'none' } : {}),
    ...dragActiveStyle(dragState, id),
  };

  return (
    <th
      {...restProps}
      ref={registerRef}
      style={thStyle}
      {...attributes}
      {...listeners}
    >
      {children}
      {nextId && <ResizeHandle onMouseDown={onResizeMouseDown} />}
    </th>
  );
};

// Resize-only header cell: no `@dnd-kit`, no DndContext needed — this is
// what renders whenever `resizable` is on but `draggable` (Config mode)
// isn't, so every grid can have its columns resized live without ever
// entering Config mode.
const ResizeOnlyHeaderCell = (props) => {
  const { id, nextId, thRefs, style, children, ...restProps } = props;
  const registerRef = (node) => {
    if (thRefs) thRefs.current[id] = node;
  };
  const onResizeMouseDown = useAdjacentColumnResizeHandle(id, nextId, thRefs);

  return (
    <th {...restProps} ref={registerRef} style={{ ...style, position: 'relative' }}>
      {children}
      {nextId && <ResizeHandle onMouseDown={onResizeMouseDown} />}
    </th>
  );
};

const buildColumns = (headers) =>
  headers.map((column, i) => {
    const dragKey = `drag-col-${i}`;
    return {
      ...column,
      dataIndex: column.key,
      dragKey,
      onHeaderCell: () => ({ id: dragKey }),
      onCell: () => ({ id: dragKey }),
    };
  });

const DataTable = forwardRef(
  (
    {
      headers = [],
      data = [],
      tableSize = "small",
      totalRecords = 0,
      bordered = true,
      isAPiCallLoading = false,
      paginationPosition = "bottomRight",
      onRowClick: onRowClickFn,
      variant,
      multipleRowSelection = false,
      style = {},
      pagination = null,
      setPagination,
      noDataMessage = "No Data Found",
      defaultSelectedRows = [],
      className = "",
      selectionColumnVisible = false,
      rowUniqueId = "id",
      allowTableSorting = false,
      rowHoverable = true,
      virtual = false,
      tableMaxWidth = 3000,
      tableMaxHeight = 450,
      onTableChange,
      autoResetSelection = true,
      onPageChange = null,
      fixedHeader = true,
      serverSideSorting = false,
      // `draggable` turns on column REORDER (+ resize) — this is the
      // Config-mode flag, e.g. `draggable={isConfiguring}`. Off by default,
      // same as before the Config feature existed.
      draggable = false,
      // `resizable` turns on column-width resize alone, independent of
      // Config mode — on by default so every DataTable consumer can resize
      // columns even without ever clicking Config. When `draggable` is on,
      // resize is always available too regardless of this flag.
      resizable = true,
      ...props
    },
    ref,
  ) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [sortedInfo, setSortedInfo] = useState({});
    const [dragIndex, setDragIndex] = useState({ active: -1, over: -1 });
    const [baseColumns, setBaseColumns] = useState(() => buildColumns(headers ?? []));

    const lastPointerX = useRef(0);
    // dragKey -> <th> DOM node, populated by each header cell's ref — read
    // by useAdjacentColumnResizeHandle to get both sides of a divider's
    // current rendered width at drag start (see that hook for why resize is
    // pairwise rather than per-column).
    const thRefs = useRef({});

    const [colWidths, setColWidths] = useState({});

    useEffect(() => {
      if (!draggable && !resizable) return;
      const container = document.querySelector('.data-table-main');
      if (!container) return;

      const handleColResize = (e) => {
        const { widths } = e.detail;
        setColWidths((prev) => ({ ...prev, ...widths }));
      };

      container.addEventListener('col-resize', handleColResize);
      return () => container.removeEventListener('col-resize', handleColResize);
    }, [draggable, resizable]);

    useEffect(() => {
      if (autoResetSelection) {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        onSelectChange([], []);
      }
    }, [data, isAPiCallLoading, autoResetSelection]);

    useEffect(() => {
      setBaseColumns(buildColumns(headers ?? []));
    }, [headers]);

    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
    );

    const resetAll = () => {
      setSelectedRowKeys([]);
      setSelectedRows([]);
      onSelectChange([], []);
      setSortedInfo(null);
    };

    // Programmatically selects the given rows (defaults to every currently
    // loaded row) — used by consumers that need an external "Select All"
    // button rather than relying on antd's built-in selection-column checkbox.
    const selectAll = (rows = data) => {
      const keys = rows.map((row) => row[rowUniqueId]);
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
      if (multipleRowSelection) {
        onRowClickFn?.(rows);
      }
    };

    useImperativeHandle(ref, () => ({
      resetAll,
      selectAll,
      // Returns the current column order + widths, e.g.
      // [{ key: "dateAdded", width: 180 }, ...] — used by
      // useGridColumnConfig to persist on "Save Config". colWidths is keyed
      // by the internal dragKey (not the real column key), so it's mapped
      // back here; any column never resized this session falls back to
      // whatever width its header entry already carried.
      getColumnLayout: () =>
        baseColumns.map((col) => ({
          key: col.key,
          width: colWidths[col.dragKey] ?? col.width,
        })),
      // Discards any in-progress drag/resize changes, reverting to what
      // the `headers` prop currently describes — used on "discard".
      resetColumnLayout: () => {
        setBaseColumns(buildColumns(headers ?? []));
        setColWidths({});
      },
    }));

    const DTSortIcon = ({ iconType }) => {
      switch (iconType) {
        case "ascend":
          return (
            <span className="custom-sort-icon">
              <CaretUpOutlined />
              <CaretUpOutlined />
            </span>
          );
        case "descend":
          return (
            <span className="custom-sort-icon">
              <CaretDownOutlined />
              <CaretDownOutlined />
            </span>
          );
        default:
          return (
            <span className="custom-sort-icon custom-sort-default">
              <CaretUpOutlined />
              <CaretDownOutlined />
            </span>
          );
      }
    };

    const columns = useMemo(() => {
      return baseColumns.map(({ key, text, sorter, render, ellipsis, width, fixed, dragKey }, i) => {
        // Computed fresh from the CURRENT baseColumns order (not the static
        // one buildColumns set at mount) so it stays correct after a
        // reorder drag — see useAdjacentColumnResizeHandle for why the
        // resize handle needs to know its right-hand neighbor.
        const nextDragKey = baseColumns[i + 1]?.dragKey;
        const onHeaderCell = () => ({ id: dragKey, nextId: nextDragKey, thRefs });
        const onCell = () => ({ id: dragKey });
        const sortMethod = (a, b) => {
          const valueA = a[key];
          const valueB = b[key];

          if (valueB == null || valueB === "") return 1;
          if (valueA == null || valueA === "") return -1;

          let dateA, dateB;

          if (typeof valueA === "string") dateA = Date.parse(valueA);
          if (typeof valueB === "string") dateB = Date.parse(valueB);

          if (!isNaN(dateA) && !isNaN(dateB)) {
            return dateA - dateB;
          }

          const isPureNumber = (val) => {
            return (
              typeof val === "number" ||
              (typeof val === "string" && /^\d+(\.\d+)?$/.test(val.trim()))
            );
          };

          const isNumberA = isPureNumber(valueA);
          const isNumberB = isPureNumber(valueB);

          if (isNumberA && isNumberB) {
            return parseFloat(valueA) - parseFloat(valueB);
          }

          return valueA.toString().localeCompare(valueB.toString(), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        };

        return {
          title: text,
          dataIndex: key,
          key,
          render,
          ellipsis,
          width,
          fixed,
          dragKey,
          onHeaderCell,
          onCell,
          // sorter: allowTableSorting && data.length > 1 ? sorter || sortMethod : false,
          sorter:
            allowTableSorting && data.length > 1
              ? serverSideSorting
                ? true // tells Ant Design "sortable" but disables its own comparator
                : sorter || sortMethod
              : false,
          sortOrder: sortedInfo?.columnKey === key ? sortedInfo?.order : null,
          showSorterTooltip: { target: "sorter-icon" },
          sortIcon: ({ sortOrder }) => {
            switch (sortOrder) {
              case "ascend":
                return <DTSortIcon iconType="ascend" />;
              case "descend":
                return <DTSortIcon iconType="descend" />;
              default:
                return <DTSortIcon iconType="default" />;
            }
          },
        };
      });
    }, [baseColumns, allowTableSorting, data, sortedInfo, colWidths, draggable]); 

    const onSelectChange = (
      currentSelectedRowKeys,
      currentSelectedRows,
      index = -1,
    ) => {
      setSelectedRowKeys(currentSelectedRowKeys);
      setSelectedRows(currentSelectedRows);

      // Multi-select consumers need to know when the selection drops back to
      // zero (e.g. unchecking the last row), so that branch always fires.
      // Single-select (radio) consumers keep the original behavior.
      if (multipleRowSelection) {
        onRowClickFn?.(currentSelectedRows, index);
      } else if (currentSelectedRows.length > 0) {
        onRowClickFn?.(currentSelectedRows[0], index);
      }
    };

    const paginationConfig = useMemo(() => {
      const isHaveMorePages =
        totalRecords > Math.min(...["25", "50", "100"].map(Number));

      return pagination
        ? {
          current: pagination.pageIndex + 1,
          pageSize: pagination.pageSize,
          total: totalRecords,
          onChange: (page, pSize) => {
            setPagination?.((prev) => ({
              ...prev,
              pageIndex: page - 1,
            }));

              onPageChange?.(page - 1, pSize);
              onSelectChange([], []);
            },
            position: [paginationPosition],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            showSizeChanger: isHaveMorePages,
            pageSizeOptions: ["25", "50", "100"],
            onShowSizeChange: (current, size) => {
              setPagination?.((prev) => ({
                ...prev,
                pageSize: size,
              }));
            },
            itemRender: (page, type, originalElement) => {
              if (!isHaveMorePages && type) return null;
              if (!isHaveMorePages && page) return null;
              return originalElement;
            },
            showQuickJumper: true,
            locale: {
              items_per_page: "",
            },
          }
        : false;
    }, [pagination, paginationPosition, totalRecords, setPagination]);

    const handleTableChange = useCallback(
      (pagination, filters, sorter) => {
        setSortedInfo(sorter?.order ? sorter : null);

        if (setPagination) {
          setPagination({
            pageIndex: pagination.current - 1,
            pageSize: pagination.pageSize,
          });
        }

        setSelectedRowKeys([]);
        onSelectChange([], []);

        if (onTableChange) {
          onTableChange(pagination, filters, sorter);
        }
      },
      [setPagination, onTableChange],
    );

    const rowSelection = {
      selectedRowKeys,
      onChange: onSelectChange,
      selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
      getCheckboxProps: (record) => ({
        disabled: record?.disabled,
      }),
      columnWidth: selectionColumnVisible ? 50 : 0,
      renderCell: selectionColumnVisible ? undefined : () => null,
      columnTitle: selectionColumnVisible ? undefined : <></>,
    };

    const onRowHandler = (record, index) => {
      // console.log("onRowHandler called");
      // console.log("Row record:", record);
      // console.log("Row index:", index);

      return {
        onClick: (event) => {
          // console.log("Row clicked");
          // console.log("Event object:", event);

          const targetElement = event.target;
          // console.log("Clicked element:", targetElement);
          // console.log("Tag name:", targetElement.tagName);

          if (
            ["BUTTON", "A"].includes(targetElement.tagName) ||
            targetElement.closest("button") ||
            targetElement.closest("a") ||
            targetElement.hasAttribute("onClick")
          ) {
            // console.log("Click ignored because target is button/link or has onClick");
            return;
          }

          const rowKey = record[rowUniqueId];
          // console.log("Row unique key:", rowKey);

          let updatedSelectedRowKeys;
          // console.log("multipleRowSelection:", multipleRowSelection);

          if (!multipleRowSelection) {
            // console.log("Single row selection mode");
            updatedSelectedRowKeys = [rowKey];
          } else {
            // console.log("Multiple row selection mode");
            // console.log("Current selectedRowKeys:", selectedRowKeys);

            const alreadySelected = selectedRowKeys.includes(rowKey);
            // console.log("Is row already selected?", alreadySelected);

            updatedSelectedRowKeys = alreadySelected
              ? selectedRowKeys.filter((key) => key !== rowKey)
              : [...selectedRowKeys, rowKey];

            // console.log("Updated selectedRowKeys:", updatedSelectedRowKeys);
          }

          const updatedSelectedRows = data.filter((item) =>
            updatedSelectedRowKeys.includes(item[rowUniqueId]),
          );

          // console.log("Updated selected rows:", updatedSelectedRows);

          setSelectedRowKeys(updatedSelectedRowKeys);
          // console.log("setSelectedRowKeys called");

          setSelectedRows(updatedSelectedRows);
          // console.log("setSelectedRows called");

          if (multipleRowSelection) {
            onRowClickFn?.(updatedSelectedRows);
          } else {
            onRowClickFn?.(record);
          }
        },

        onDoubleClick: () => {
          // console.log("Row double clicked");
          // console.log("Record passed to onRowClickFn:", record);
          // console.log("Index passed to onRowClickFn:", index);

          onRowClickFn?.(record, index);

          // console.log("onRowClickFn executed (if defined)");
        },

        style: {
          cursor: rowHoverable ? "pointer" : "default",
        },
      };
    };

    const getClassNames = () =>
      [
        className,
        variant === "outlined" ? "table-outlined" : "",
        fixedHeader ? "table-has-fixed-header" : "",
      ]
        .filter(Boolean)
        .join(" ");

    const onDragMove = ({ activatorEvent, delta }) => {
      lastPointerX.current = (activatorEvent?.clientX ?? 0) + delta.x;
    };

    const onDragEnd = ({ active, over }) => {
      if (!active) {
        setDragIndex({ active: -1, over: -1 });
        return;
      }

      setBaseColumns((prev) => {
        const activeIndex = prev.findIndex((c) => c.dragKey === active?.id);
        let targetIndex;

        if (!over) {
          const tableEl = document.querySelector(".data-table-main");
          const tableRect = tableEl?.getBoundingClientRect();

          if (tableRect) {
            const tableMidX = tableRect.left + tableRect.width / 2;
            targetIndex = lastPointerX.current < tableMidX ? 0 : prev.length - 1;
          } else {
            targetIndex = activeIndex;
          }
        } else if (active.id !== over.id) {
          targetIndex = prev.findIndex((c) => c.dragKey === over?.id);
        } else {
          targetIndex = activeIndex;
        }

        return arrayMove(prev, activeIndex, targetIndex);
      });

      setDragIndex({ active: -1, over: -1 });
    };

    const onDragOver = ({ active, over }) => {
      const activeIndex = baseColumns.findIndex((c) => c.dragKey === active?.id);
      const overIndex = baseColumns.findIndex((c) => c.dragKey === over?.id);
      setDragIndex({
        active: active?.id,
        over: over?.id ?? -1,
        direction: overIndex >= 0 && overIndex > activeIndex ? 'right' : 'left',
      });
    };

    const activeColumn = baseColumns.find((c) => c.dragKey === dragIndex.active);

    const tableNode = (
      <StyledTableContainer className="data-table-main">
        <Table
          className={getClassNames()}
          columns={columns}
          components={
            draggable
              ? {
                  header: { cell: ResizableHeaderCell },
                  body: { cell: TableBodyCell },
                }
              : resizable
                ? { header: { cell: ResizeOnlyHeaderCell } }
                : undefined
          }
          dataSource={data}
          size={tableSize}
          bordered={bordered}
          rowSelection={
            selectionColumnVisible
              ? {
                type: multipleRowSelection ? "checkbox" : "radio",
                ...rowSelection,
              }
              : rowSelection
          }
          rowKey={rowUniqueId}
          pagination={paginationConfig}
          loading={{
            spinning: isAPiCallLoading,
            tip: "Loading",
          }}
          scroll={{
            x: virtual ? tableMaxWidth : "max-content",
            y: virtual ? tableMaxHeight : null,
          }}
          onRow={onRowHandler}
          onChange={handleTableChange}
          style={style}
          locale={{
            emptyText: noDataMessage,
          }}
          virtual={virtual}
          tableLayout="auto"
          {...props}
        />
      </StyledTableContainer>
    )

    if (!draggable) {
      return tableNode;
    }

    return (
      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={columns.map((c) => c.dragKey)}
          strategy={horizontalListSortingStrategy}
        >
          <DragIndexContext.Provider value={dragIndex}>
            {tableNode}
          </DragIndexContext.Provider>
        </SortableContext>

        <DragOverlay>
          <th style={{
            backgroundColor: '#76C4E8',
            padding: 8,
            border: '1px solid #24709d',
            borderRadius: 4,
            boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.5)'
          }}>
            {activeColumn?.text}
          </th>
        </DragOverlay>
      </DndContext>
    );
  },
);

export default DataTable;